import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ForceGraph from 'force-graph';
import { useAuthStore } from '../stores/authStore';

interface KnowledgeGraphProps {
  className?: string;
  onNodeClick?: (nodeData: any) => void;
}

interface GraphNode {
  id: string;
  name: string;
  category: string;
  val: number;
  color: string;
  data: any;
  isGhost?: boolean; // Ghost node for entities
}

interface GraphLink {
  source: string;
  target: string;
  color: string;
  width: number;
  isAssetLink?: boolean;
  isEntityLink?: boolean; // Link to entity ghost node
}

interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

export default function KnowledgeGraph({ className = '', onNodeClick }: KnowledgeGraphProps) {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<any>(null);
  
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [isSimulationReady, setIsSimulationReady] = useState(false); // Track when graph is ready to show
  const highlightNodesRef = useRef<Set<string>>(new Set()); // Nodes to highlight (hovered + neighbors)
  const highlightLinksRef = useRef<Set<string>>(new Set()); // Links to highlight
  const hoverNodeRef = useRef<string | null>(null); // Currently hovered node

  // Initialize force-graph once when data loads
  useEffect(() => {
    if (!containerRef.current) return;
    if (graphData.nodes.length === 0) return;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    if (width === 0 || height === 0) return;

    console.log('Initializing graph:', {
      nodes: graphData.nodes.length,
      links: graphData.links.length,
      dimensions: { width, height }
    });

    // Reset ready state when data changes
    setIsSimulationReady(false);

    // Clean up existing graph
    if (graphRef.current) {
      graphRef.current._destructor?.();
      graphRef.current = null;
    }
    
    // Clear container
    containerRef.current.innerHTML = '';

    // Detect dark mode
    const isDarkMode = document.documentElement.classList.contains('dark');
    const bgColor = isDarkMode ? '#000000' : '#f9fafb';
    
    // Build link lookup for highlighting
    const linkSet = new Set<string>();
    graphData.links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? (link.source as any).id : link.source;
      const targetId = typeof link.target === 'object' ? (link.target as any).id : link.target;
      linkSet.add(`${sourceId}-${targetId}`);
      linkSet.add(`${targetId}-${sourceId}`);
    });
    
    const graph = (ForceGraph as any)()(containerRef.current)
      .width(width)
      .height(height)
      .backgroundColor(bgColor)
      .graphData(graphData)
      .nodeLabel((node: any) => node.name)
      .nodeVal((node: any) => node.val)
      .nodeCanvasObject((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
        const label = node.name;
        const fontSize = 11 / globalScale;
        ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        
        // Check highlight state
        const isHighlighted = highlightNodesRef.current.has(node.id);
        const isHovered = hoverNodeRef.current === node.id;
        const hasHover = hoverNodeRef.current !== null;
        const shouldDim = hasHover && !isHighlighted;
        
        // Ghost nodes (entities) are rendered differently
        if (node.isGhost) {
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.val, 0, 2 * Math.PI, false);
          ctx.fillStyle = shouldDim ? 'rgba(156, 163, 175, 0.1)' : 'rgba(156, 163, 175, 0.4)';
          ctx.fill();
          
          ctx.setLineDash([2, 2]);
          ctx.strokeStyle = shouldDim ? 'rgba(156, 163, 175, 0.15)' : 'rgba(156, 163, 175, 0.6)';
          ctx.lineWidth = 1 / globalScale;
          ctx.stroke();
          ctx.setLineDash([]);
          
          if (globalScale > 0.8 && !shouldDim) {
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillStyle = isDarkMode ? 'rgba(156, 163, 175, 0.8)' : 'rgba(107, 114, 128, 0.8)';
            const truncatedLabel = label.length > 20 ? label.substring(0, 20) + '...' : label;
            ctx.fillText(truncatedLabel, node.x, node.y + node.val + 3);
          }
        } else {
          // Regular node - size increases when highlighted
          const nodeRadius = isHighlighted ? node.val * 1.3 : node.val;
          
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
          
          // Color: dimmed, normal, or highlighted
          if (shouldDim) {
            ctx.fillStyle = isDarkMode ? 'rgba(55, 65, 81, 0.25)' : 'rgba(209, 213, 219, 0.4)';
          } else {
            ctx.fillStyle = node.color;
          }
          ctx.fill();
          
          // Border
          if (isHovered) {
            ctx.strokeStyle = '#F59E0B'; // Orange for hovered node
            ctx.lineWidth = 3 / globalScale;
          } else if (isHighlighted) {
            ctx.strokeStyle = '#60A5FA'; // Blue for connected nodes
            ctx.lineWidth = 2.5 / globalScale;
          } else if (shouldDim) {
            ctx.strokeStyle = isDarkMode ? 'rgba(55, 65, 81, 0.2)' : 'rgba(209, 213, 219, 0.3)';
            ctx.lineWidth = 1 / globalScale;
          } else {
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.5 / globalScale;
          }
          ctx.stroke();
          
          // Label - always show for highlighted nodes
          if ((globalScale > 0.6 || isHighlighted) && !shouldDim) {
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillStyle = isHighlighted 
              ? (isDarkMode ? '#ffffff' : '#111827')
              : (isDarkMode ? '#ffffff' : '#374151');
            if (isHighlighted) {
              ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
            }
            const truncatedLabel = label.length > 25 ? label.substring(0, 25) + '...' : label;
            ctx.fillText(truncatedLabel, node.x, node.y + nodeRadius + 4);
          }
        }
      })
      .linkCanvasObject((link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        const linkKey = `${sourceId}-${targetId}`;
        
        const isHighlighted = highlightLinksRef.current.has(linkKey);
        const hasHover = hoverNodeRef.current !== null;
        const shouldDim = hasHover && !isHighlighted;
        
        // Get source and target positions
        const source = typeof link.source === 'object' ? link.source : graphData.nodes.find(n => n.id === link.source);
        const target = typeof link.target === 'object' ? link.target : graphData.nodes.find(n => n.id === link.target);
        
        if (!source || !target) return;
        
        ctx.beginPath();
        
        // Draw curved line
        const midX = (source.x + target.x) / 2;
        const midY = (source.y + target.y) / 2;
        const dx = target.x - source.x;
        const dy = target.y - source.y;
        const curvature = 0.2;
        const ctrlX = midX - dy * curvature;
        const ctrlY = midY + dx * curvature;
        
        ctx.moveTo(source.x, source.y);
        ctx.quadraticCurveTo(ctrlX, ctrlY, target.x, target.y);
        
        // Style based on state
        if (isHighlighted) {
          ctx.strokeStyle = '#F59E0B'; // Orange for highlighted links
          ctx.lineWidth = 2.5;
        } else if (shouldDim) {
          ctx.strokeStyle = isDarkMode ? 'rgba(55, 65, 81, 0.1)' : 'rgba(209, 213, 219, 0.2)';
          ctx.lineWidth = 0.5;
        } else {
          ctx.strokeStyle = link.color || '#D1D5DB';
          ctx.lineWidth = 1;
        }
        
        if (link.isAssetLink && !isHighlighted) {
          ctx.setLineDash([3, 3]);
        }
        
        ctx.stroke();
        ctx.setLineDash([]);
      })
      .linkDirectionalArrowLength(0)
      .linkDirectionalArrowRelPos(1)
      .onNodeClick((node: any) => {
        console.log('Node clicked:', node);
        
        // Ghost nodes (entities) don't navigate - just show info
        if (node.isGhost) {
          console.log('Entity clicked:', node.data);
          return;
        }
        
        if (onNodeClick) {
          onNodeClick(node.data);
        }
        
        // Use navigate for smooth transition without page reload
        navigate(`/library?highlight=${node.data.id}`);
      })
      .onNodeDrag((node: any) => {
        // During drag, fix the node position
        node.fx = node.x;
        node.fy = node.y;
      })
      .onNodeDragEnd((node: any) => {
        // Keep node fixed after drag - user's adjustments stay
        node.fx = node.x;
        node.fy = node.y;
      })
      .onNodeHover((node: any) => {
        // Update highlight sets for lineage visualization
        highlightNodesRef.current.clear();
        highlightLinksRef.current.clear();
        
        if (node) {
          console.log('Hovering node:', node.id); // Debug
          hoverNodeRef.current = node.id;
          highlightNodesRef.current.add(node.id);
          
          // Find all connected nodes and links
          graphData.links.forEach(link => {
            const sourceId = typeof link.source === 'object' ? (link.source as any).id : link.source;
            const targetId = typeof link.target === 'object' ? (link.target as any).id : link.target;
            
            if (sourceId === node.id) {
              highlightNodesRef.current.add(targetId);
              highlightLinksRef.current.add(`${sourceId}-${targetId}`);
              highlightLinksRef.current.add(`${targetId}-${sourceId}`);
            } else if (targetId === node.id) {
              highlightNodesRef.current.add(sourceId);
              highlightLinksRef.current.add(`${sourceId}-${targetId}`);
              highlightLinksRef.current.add(`${targetId}-${sourceId}`);
            }
          });
          
          console.log('Highlighted nodes:', highlightNodesRef.current.size); // Debug
          console.log('Highlighted links:', highlightLinksRef.current.size); // Debug
        } else {
          hoverNodeRef.current = null;
        }
        
        // Force graph to redraw with new highlight state
        if (graphRef.current) {
          graphRef.current.nodeColor(graphRef.current.nodeColor()); // Trigger redraw
        }
        
        // Update cursor
        if (containerRef.current) {
          containerRef.current.style.cursor = node ? 'pointer' : 'default';
        }
      })
      .cooldownTicks(200)
      .d3AlphaDecay(0.05) // Faster cooling = less movement after settling
      .d3VelocityDecay(0.6) // Higher friction = slower, more controlled movement
      .warmupTicks(100)
      .enableNodeDrag(true)
      .enableZoomInteraction(true)
      .enablePanInteraction(true);

    // Configure D3 force simulation for spacious, readable layout
    graph.d3Force('charge')?.strength(-150); // More repulsion = nodes spread apart
    graph.d3Force('link')?.distance(80); // Longer link distance for breathing room
    graph.d3Force('center')?.strength(0.05); // Very gentle pull to center

    graphRef.current = graph;
    
    // Obsidian-style loading: zoom to fit first, then fade in
    // Wait for simulation to settle before showing
    setTimeout(() => {
      if (graphRef.current) {
        // Zoom to fit with less padding so nodes appear larger
        graphRef.current.zoomToFit(0, 40); // Instant zoom, tighter padding
        
        // Bump zoom in a bit more so nodes/edges aren't tiny
        setTimeout(() => {
          if (graphRef.current) {
            const currentZoom = graphRef.current.zoom();
            graphRef.current.zoom(currentZoom * 1.35, 300); // 35% closer, smooth 300ms
          }
          setTimeout(() => {
            setIsSimulationReady(true);
          }, 100);
        }, 50);
      }
    }, 600); // Wait for simulation to mostly settle

    // Handle window resize
    const handleResize = () => {
      if (containerRef.current && graphRef.current) {
        const newWidth = containerRef.current.clientWidth;
        const newHeight = containerRef.current.clientHeight;
        if (newWidth > 0 && newHeight > 0) {
          graphRef.current.width(newWidth).height(newHeight);
        }
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [graphData, onNodeClick]);

  // Category color mapping
  const getCategoryColor = (category: string) => {
    const normalizedCategory = (category || 'other').toLowerCase();
    const colors: { [key: string]: string } = {
      technology: '#3B82F6',
      business: '#10B981', 
      science: '#8B5CF6',
      health: '#EF4444',
      education: '#F59E0B',
      entertainment: '#64748B',
      other: '#6B7280'
    };
    return colors[normalizedCategory] || colors.other;
  };

  // Fetch knowledge graph data
  useEffect(() => {
    const fetchKnowledgeGraphData = async () => {
      try {
        console.log('Fetching knowledge graph data...');
        
        if (!token) {
          console.log('No token available');
          return;
        }
        
        const response = await fetch(`/api/content/graph/data?t=${Date.now()}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        });

        if (!response.ok) {
          console.log('Graph API failed');
          return;
        }

        const result = await response.json();
        const data = result.data || { nodes: [], edges: [], clusters: [] };
        
        console.log('Graph data received:', {
          nodes: data.nodes?.length || 0,
          edges: data.edges?.length || 0,
        });
        
        if (!data.nodes || data.nodes.length === 0) {
          setGraphData({ nodes: [], links: [] });
          return;
        }

        // Transform to react-force-graph format
        const transformedNodes: GraphNode[] = data.nodes.map((node: any) => {
          const connectionCount = node.connection_count || 0;
          const isAsset = node.tags?.includes('asset') || node.tags?.includes('screenshot') || node.tags?.includes('note');
          
          // Asset nodes are smaller and gray
          let nodeSize = 6; // Base size (increased)
          let nodeColor = getCategoryColor(node.category);
          
          if (isAsset) {
            nodeSize = 4; // Smaller for assets
            nodeColor = '#9CA3AF'; // Gray for assets
          } else {
            // Regular content nodes - size based on connections
            if (connectionCount >= 3) nodeSize = 12;
            else if (connectionCount >= 2) nodeSize = 10;
            else if (connectionCount >= 1) nodeSize = 8;
          }
          
          return {
            id: node.id.toString(),
            name: node.title || 'Untitled',
            category: node.category || 'other',
            val: nodeSize,
            color: nodeColor,
            data: {
              id: node.id,
              title: node.title,
              category: node.category,
              tags: node.tags || [],
              summary: node.summary || '',
              connection_count: connectionCount,
              is_connected: node.is_connected || false,
              is_asset: isAsset
            }
          };
        });

        // Transform edges to links - only include links where both nodes exist
        const nodeIds = new Set(transformedNodes.map(n => n.id));
        
        // Log how many edges we're filtering
        const totalEdges = data.edges?.length || 0;
        const validEdges = (data.edges || []).filter((edge: any) => {
          const sourceId = edge.source_content_id?.toString();
          const targetId = edge.target_content_id?.toString();
          return sourceId && targetId && nodeIds.has(sourceId) && nodeIds.has(targetId);
        });
        
        if (totalEdges !== validEdges.length) {
          console.log(`Filtered ${totalEdges - validEdges.length} edges with missing nodes (${validEdges.length}/${totalEdges} valid)`);
        }
        
        const transformedLinks: GraphLink[] = validEdges
          .map((edge: any) => {
            // Color based on relationship type
            const relationshipColors: { [key: string]: string } = {
              'same_topic': '#93C5FD',      // Lighter blue (more subtle)
              'cross_category': '#86EFAC',   // Lighter green
              'related_topic': '#C4B5FD',   // Lighter purple
              'similar': '#D1D5DB',          // Light gray (very subtle)
              'has_asset': '#D1D5DB'         // Light gray for assets
            };
            
            const color = relationshipColors[edge.relationship_type] || '#D1D5DB';
            const width = 1; // Thin lines like Obsidian
            
            return {
              source: edge.source_content_id.toString(),
              target: edge.target_content_id.toString(),
              color: color,
              width: width,
              isAssetLink: edge.relationship_type === 'has_asset'
            };
          });

        // Add ghost nodes for entities (Recall-style)
        const ghostNodes: GraphNode[] = [];
        const entityLinks: GraphLink[] = [];
        
        if (data.entities && Array.isArray(data.entities)) {
          data.entities.forEach((entity: any, idx: number) => {
            const entityId = `entity-${idx}-${entity.name.toLowerCase().replace(/\s+/g, '-')}`;
            
            // Create ghost node
            ghostNodes.push({
              id: entityId,
              name: entity.name,
              category: entity.type || 'concept',
              val: 4, // Smaller than regular nodes
              color: '#9CA3AF', // Grey
              isGhost: true,
              data: {
                id: entityId,
                title: entity.name,
                type: entity.type,
                isEntity: true,
                sourceIds: entity.sourceIds
              }
            });
            
            // Create links from entity to its source content
            (entity.sourceIds || []).forEach((sourceId: string) => {
              if (nodeIds.has(sourceId.toString())) {
                entityLinks.push({
                  source: sourceId.toString(),
                  target: entityId,
                  color: 'rgba(156, 163, 175, 0.3)', // Very subtle grey
                  width: 0.5,
                  isEntityLink: true
                });
              }
            });
          });
          
          console.log(`Added ${ghostNodes.length} ghost nodes, ${entityLinks.length} entity links`);
        }

        const transformedData = {
          nodes: [...transformedNodes, ...ghostNodes],
          links: [...transformedLinks, ...entityLinks]
        };

        setGraphData(transformedData);
        
        console.log('Graph data loaded:', {
          contentNodes: transformedNodes.length,
          ghostNodes: ghostNodes.length,
          contentLinks: transformedLinks.length,
          entityLinks: entityLinks.length
        });

      } catch (error) {
        console.error('Error fetching knowledge graph:', error);
        setGraphData({ nodes: [], links: [] });
      }
    };

    fetchKnowledgeGraphData();
  }, [token]);

  return (
    <div className={className} style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Force Graph Container - fades in when ready */}
      <div 
        ref={containerRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          opacity: isSimulationReady ? 1 : 0,
          transition: 'opacity 0.3s ease-out'
        }} 
      />
    </div>
  );
}
